// GENERATED from the backend by scripts/sync-shared-helpers.js; edit the backend file, then re-run it.
/**
 * Survey logic expressions (2026-10-22).
 *
 * One small JSON AST drives display logic, skip logic and probe conditions in the
 * Survey Builder. This module is the only place that understands it:
 *   validateLogic   structural checks against the questionnaire (unknown keys,
 *                   forward references, unknown option codes, bad shapes)
 *   evaluateLogic   evaluates against a respondent's answers (server-side
 *                   authority for submit, voice extraction, tests)
 *   toSurveyJsExpression  compiles to the SurveyJS expression string the form
 *                   runtime (survey-core) evaluates in the browser and headless
 *   describeLogic   renders English for the voice prompt and the Builder
 *   collectRefs     every ref the expression reads
 *
 * Grammar (constants/surveyQuant.js LOGIC_OPS):
 *   { op: 'and'|'or', args: [expr, ...] }          { op: 'not', args: [expr] }
 *   { op: 'eq'|'neq'|'gt'|'gte'|'lt'|'lte', ref, v }
 *   { op: 'in'|'not_in'|'contains_any'|'contains_all', ref, v: [..] }
 *   { op: 'between', ref, v: [low, high] }         { op: 'answered'|'not_answered', ref }
 * refs: 'q<key>' | 'q<key>.other' | 'q<key>.<row>' (grid row) | 'demo.<field>' |
 *       'at.<code>' | 'meta.<field>' | 'arm.<split key>' (split sample arm, Phase 5)
 *
 * The frontend carries a copy of this file (src/utils/surveyLogic.js) generated by
 * scripts/sync-shared-helpers.js; edit here, then run that script.
 */

const LOGIC_OPS = [
  'and', 'or', 'not',
  'eq', 'neq', 'in', 'not_in', 'contains_any', 'contains_all',
  'gt', 'gte', 'lt', 'lte', 'between',
  'answered', 'not_answered',
];
const BOOL_OPS = ['and', 'or', 'not'];
const LIST_OPS = ['in', 'not_in', 'contains_any', 'contains_all'];
const CMP_OPS = ['gt', 'gte', 'lt', 'lte'];
const UNARY_OPS = ['answered', 'not_answered'];
const MULTI_VALUE_TYPES = ['multi_choice', 'ranking', 'grid_multi'];
const OPTION_TYPES = ['single_choice', 'multi_choice', 'dropdown', 'likert', 'ranking'];
const MAX_DEPTH = 10;

/* ----------------------------------------------------------------------- */
/* refs                                                                    */
/* ----------------------------------------------------------------------- */

/** 'q3' -> { kind:'question', key:'q3', sub:null }; 'q4.other' -> sub 'other'; 'demo.age' -> kind 'demo'. */
function parseRef(ref) {
  const s = String(ref || '').trim();
  if (!s) return null;
  const dot = s.indexOf('.');
  const head = dot === -1 ? s : s.slice(0, dot);
  const sub = dot === -1 ? null : s.slice(dot + 1);
  if (head === 'demo' || head === 'at' || head === 'meta' || head === 'arm') return sub ? { kind: head, key: sub, sub: null } : null;
  if (/^q[A-Za-z0-9_]+$/.test(head)) return { kind: 'question', key: head, sub };
  return null;
}

function collectRefs(expr, out = []) {
  if (!expr || typeof expr !== 'object') return out;
  if (BOOL_OPS.includes(expr.op)) (expr.args || []).forEach((a) => collectRefs(a, out));
  else if (expr.ref && !out.includes(expr.ref)) out.push(expr.ref);
  return out;
}

/* ----------------------------------------------------------------------- */
/* validate                                                                */
/* ----------------------------------------------------------------------- */

/**
 * @param expr the AST (null is valid: no condition)
 * @param ctx  { questionKeys: ['q1', ...] in order, currentKey, allowForward,
 *               typeOf(key) -> question_type, optionCodes(key) -> [codes] | null,
 *               gridRows(key) -> [codes] | null, where: 'display'|'skip'|'probe',
 *               parentKey (probe: the only question a probe may read),
 *               arms: { splitKey: [arm codes] } (the survey's split samples) }
 * @returns string[] problems (empty = valid)
 */
function validateLogic(expr, ctx = {}, path = 'logic', depth = 0) {
  const problems = [];
  if (expr === null || expr === undefined) return problems;
  if (typeof expr !== 'object' || Array.isArray(expr)) return [`${path}: must be an object`];
  if (depth > MAX_DEPTH) return [`${path}: nested too deep (max ${MAX_DEPTH})`];
  const { op } = expr;
  if (!LOGIC_OPS.includes(op)) return [`${path}: unknown op "${op}"`];

  if (BOOL_OPS.includes(op)) {
    const args = expr.args;
    if (!Array.isArray(args) || args.length === 0) return [`${path}: "${op}" needs args`];
    if (op === 'not' && args.length !== 1) return [`${path}: "not" takes exactly one argument`];
    args.forEach((a, i) => problems.push(...validateLogic(a, ctx, `${path}.args[${i}]`, depth + 1)));
    return problems;
  }

  const ref = parseRef(expr.ref);
  if (!ref) return [`${path}: bad ref "${expr.ref}"`];
  if (ref.kind === 'question') {
    const keys = ctx.questionKeys || [];
    const idx = keys.indexOf(ref.key);
    if (keys.length && idx === -1) problems.push(`${path}: unknown question "${ref.key}"`);
    if (ctx.where === 'probe' && ctx.parentKey && ref.key !== ctx.parentKey) {
      problems.push(`${path}: a probe may only read its own question (${ctx.parentKey}), not ${ref.key}`);
    }
    if (idx !== -1 && ctx.currentKey && !ctx.allowForward) {
      const cur = keys.indexOf(ctx.currentKey);
      if (ctx.where === 'display' && idx >= cur) problems.push(`${path}: "${ref.key}" is not asked before ${ctx.currentKey}`);
      if (ctx.where === 'skip' && idx > cur) problems.push(`${path}: "${ref.key}" is asked after ${ctx.currentKey}`);
    }
    if (ref.sub && ref.sub !== 'other' && typeof ctx.gridRows === 'function') {
      const rows = ctx.gridRows(ref.key);
      if (rows && !rows.includes(ref.sub)) problems.push(`${path}: "${ref.key}" has no row "${ref.sub}"`);
    }
  }

  if (ref.kind === 'arm' && ctx.arms && !ctx.arms[ref.key]) problems.push(`${path}: unknown split "${ref.key}"`);
  const qType = ref.kind === 'question' && !ref.sub && typeof ctx.typeOf === 'function' ? ctx.typeOf(ref.key) : null;
  const codes = ref.kind === 'question' && !ref.sub && typeof ctx.optionCodes === 'function' ? ctx.optionCodes(ref.key)
    : ref.kind === 'arm' && ctx.arms ? (ctx.arms[ref.key] || null) : null;
  const checkCode = (c) => {
    if (codes && !codes.map(String).includes(String(c))) problems.push(`${path}: "${ref.key}" has no ${ref.kind === 'arm' ? 'arm' : 'option'} "${c}"`);
  };

  if (UNARY_OPS.includes(op)) return problems;
  if (LIST_OPS.includes(op)) {
    if (!Array.isArray(expr.v) || expr.v.length === 0) problems.push(`${path}: "${op}" needs a non-empty list v`);
    else expr.v.forEach(checkCode);
    if ((op === 'contains_any' || op === 'contains_all') && qType && !MULTI_VALUE_TYPES.includes(qType)) {
      problems.push(`${path}: "${op}" needs a multi-value question; ${ref.key} is ${qType}`);
    }
    return problems;
  }
  if (op === 'between') {
    if (!Array.isArray(expr.v) || expr.v.length !== 2 || expr.v.some((n) => !Number.isFinite(Number(n)))) {
      problems.push(`${path}: "between" needs v: [low, high]`);
    }
    return problems;
  }
  if (CMP_OPS.includes(op)) {
    if (!Number.isFinite(Number(expr.v))) problems.push(`${path}: "${op}" needs a numeric v`);
    if (qType && OPTION_TYPES.includes(qType)) problems.push(`${path}: "${op}" does not apply to ${qType} (${ref.key})`);
    return problems;
  }
  // eq / neq
  if (expr.v === undefined || expr.v === null || Array.isArray(expr.v) || typeof expr.v === 'object') {
    problems.push(`${path}: "${op}" needs a single value v`);
  } else {
    checkCode(expr.v);
  }
  return problems;
}

/* ----------------------------------------------------------------------- */
/* evaluate                                                                */
/* ----------------------------------------------------------------------- */

const isEmptyValue = (v) => v === null || v === undefined || v === '' || (Array.isArray(v) && v.length === 0)
  || (typeof v === 'object' && !Array.isArray(v) && !(v instanceof Date) && Object.keys(v).length === 0);
const asList = (v) => (Array.isArray(v) ? v : isEmptyValue(v) ? [] : [v]);
const same = (a, b) => String(a) === String(b);
const num = (v) => {
  if (v === null || v === undefined || v === '' || Array.isArray(v)) return NaN;
  if (v instanceof Date) return v.getTime();
  return Number(v);
};

/**
 * Read a ref out of the answer bag. `answers` is { q1: 'a', q4: ['1','2'],
 * 'q4.other': 'text', q6: { r1: '2' }, demo: { age: 30 }, at: { '212': '5' }, meta: {...} }.
 * Values may also be keyed flat ('demo.age').
 */
function readRef(answers, ref) {
  const r = parseRef(ref);
  if (!r || !answers) return undefined;
  if (r.kind !== 'question') {
    const bag = answers[r.kind];
    if (bag && typeof bag === 'object' && r.key in bag) return bag[r.key];
    return answers[`${r.kind}.${r.key}`];
  }
  if (!r.sub) return answers[r.key];
  if (`${r.key}.${r.sub}` in answers) return answers[`${r.key}.${r.sub}`];
  if (r.sub === 'other' && `${r.key}-Comment` in answers) return answers[`${r.key}-Comment`];
  const base = answers[r.key];
  return base && typeof base === 'object' && !Array.isArray(base) ? base[r.sub] : undefined;
}

function evaluateLogic(expr, answers = {}) {
  if (expr === null || expr === undefined) return true;
  const { op } = expr;
  switch (op) {
    case 'and': return (expr.args || []).every((a) => evaluateLogic(a, answers));
    case 'or': return (expr.args || []).some((a) => evaluateLogic(a, answers));
    case 'not': return !evaluateLogic((expr.args || [])[0], answers);
    default: break;
  }
  const val = readRef(answers, expr.ref);
  switch (op) {
    case 'answered': return !isEmptyValue(val);
    case 'not_answered': return isEmptyValue(val);
    // On a multi-value answer "eq" means "includes" (it compiles to SurveyJS "contains").
    case 'eq': return asList(val).some((x) => same(x, expr.v));
    case 'neq': return !asList(val).some((x) => same(x, expr.v));
    case 'in':
    case 'contains_any': return asList(val).some((x) => (expr.v || []).some((c) => same(x, c)));
    case 'not_in': return !asList(val).some((x) => (expr.v || []).some((c) => same(x, c)));
    case 'contains_all': { const l = asList(val); return (expr.v || []).every((c) => l.some((x) => same(x, c))); }
    case 'gt': return num(val) > Number(expr.v);
    case 'gte': return num(val) >= Number(expr.v);
    case 'lt': return num(val) < Number(expr.v);
    case 'lte': return num(val) <= Number(expr.v);
    case 'between': { const n = num(val); return n >= Number(expr.v[0]) && n <= Number(expr.v[1]); }
    default: return false;
  }
}

/* ----------------------------------------------------------------------- */
/* compile to SurveyJS                                                     */
/* ----------------------------------------------------------------------- */

const sjsRef = (ref) => {
  const r = parseRef(ref);
  if (!r) return `{${ref}}`;
  if (r.kind !== 'question') return `{${r.kind}.${r.key}}`;
  if (!r.sub) return `{${r.key}}`;
  // SurveyJS stores the other-specify text as "<name>-Comment"; grid rows as {name.row}.
  return r.sub === 'other' ? `{${r.key}-Comment}` : `{${r.key}.${r.sub}}`;
};
const sjsLit = (v) => (typeof v === 'number' ? String(v) : `'${String(v).replace(/\\/g, '\\\\').replace(/'/g, "\\'")}'`);
const sjsList = (list) => `[${(list || []).map(sjsLit).join(', ')}]`;

/**
 * @param ctx { typeOf(key) -> question_type } so "eq" on a multi-value question
 *            compiles to "contains" instead of "=".
 */
function toSurveyJsExpression(expr, ctx = {}) {
  if (expr === null || expr === undefined) return '';
  const { op } = expr;
  if (op === 'and' || op === 'or') return `(${(expr.args || []).map((a) => toSurveyJsExpression(a, ctx)).join(` ${op} `)})`;
  if (op === 'not') return `!(${toSurveyJsExpression((expr.args || [])[0], ctx)})`;
  const r = parseRef(expr.ref);
  const ref = sjsRef(expr.ref);
  const multi = r && r.kind === 'question' && !r.sub && typeof ctx.typeOf === 'function' && MULTI_VALUE_TYPES.includes(ctx.typeOf(r.key));
  switch (op) {
    case 'answered': return `${ref} notempty`;
    case 'not_answered': return `${ref} empty`;
    case 'eq': return multi ? `${ref} contains ${sjsLit(expr.v)}` : `${ref} = ${sjsLit(expr.v)}`;
    case 'neq': return multi ? `${ref} notcontains ${sjsLit(expr.v)}` : `${ref} <> ${sjsLit(expr.v)}`;
    case 'in':
    case 'contains_any': return `${ref} anyof ${sjsList(expr.v)}`;
    case 'not_in': return `${ref} noneof ${sjsList(expr.v)}`;
    case 'contains_all': return `${ref} allof ${sjsList(expr.v)}`;
    case 'gt': return `${ref} > ${Number(expr.v)}`;
    case 'gte': return `${ref} >= ${Number(expr.v)}`;
    case 'lt': return `${ref} < ${Number(expr.v)}`;
    case 'lte': return `${ref} <= ${Number(expr.v)}`;
    case 'between': return `(${ref} >= ${Number(expr.v[0])} and ${ref} <= ${Number(expr.v[1])})`;
    default: return '';
  }
}

/* ----------------------------------------------------------------------- */
/* describe (English)                                                      */
/* ----------------------------------------------------------------------- */

/** Collapse ['0','1','2','3'] into '0 to 3'; mixed lists stay as 'a, b or c'. */
function listPhrase(items) {
  const nums = items.map(Number);
  if (items.length >= 3 && nums.every((n) => Number.isInteger(n))) {
    const sorted = [...nums].sort((a, b) => a - b);
    const consecutive = sorted.every((n, i) => i === 0 || n === sorted[i - 1] + 1);
    if (consecutive) return `${sorted[0]} to ${sorted[sorted.length - 1]}`;
  }
  if (items.length === 1) return items[0];
  return `${items.slice(0, -1).join(', ')} or ${items[items.length - 1]}`;
}

/**
 * @param ctx { labelOf(ref) -> 'Q3' | 'age', optionLabel(key, code) -> label | null,
 *              armLabel(splitKey, code) -> label | null }
 */
function describeLogic(expr, ctx = {}) {
  if (expr === null || expr === undefined) return 'Always';
  const { op } = expr;
  if (op === 'and') return (expr.args || []).map((a) => describeLogic(a, ctx)).join(' and ');
  if (op === 'or') return `either ${(expr.args || []).map((a) => describeLogic(a, ctx)).join(' or ')}`;
  if (op === 'not') return `not (${describeLogic((expr.args || [])[0], ctx)})`;
  const r = parseRef(expr.ref);
  const subject = typeof ctx.labelOf === 'function' ? ctx.labelOf(expr.ref) : null;
  const name = subject || (r ? (r.kind === 'question' ? `${r.key.toUpperCase()}${r.sub ? ` (${r.sub})` : ''}` : r.kind === 'arm' ? `the ${r.key} arm` : r.key) : String(expr.ref));
  const lab = (c) => {
    const l = r && r.kind === 'question' && !r.sub && typeof ctx.optionLabel === 'function' ? ctx.optionLabel(r.key, c)
      : r && r.kind === 'arm' && typeof ctx.armLabel === 'function' ? ctx.armLabel(r.key, c) : null;
    return l ? String(l) : String(c);
  };
  switch (op) {
    case 'answered': return `${name} was answered`;
    case 'not_answered': return `${name} was not answered`;
    case 'eq': return `${name} is ${lab(expr.v)}`;
    case 'neq': return `${name} is not ${lab(expr.v)}`;
    case 'in': return `${name} is ${listPhrase((expr.v || []).map(lab))}`;
    case 'not_in': return `${name} is not ${listPhrase((expr.v || []).map(lab))}`;
    case 'contains_any': return `${name} includes ${listPhrase((expr.v || []).map(lab))}`;
    case 'contains_all': return `${name} includes all of ${(expr.v || []).map(lab).join(', ')}`;
    case 'gt': return `${name} is more than ${expr.v}`;
    case 'gte': return `${name} is at least ${expr.v}`;
    case 'lt': return `${name} is less than ${expr.v}`;
    case 'lte': return `${name} is at most ${expr.v}`;
    case 'between': return `${name} is ${expr.v[0]} to ${expr.v[1]}`;
    default: return '';
  }
}

/** Shorthand for the common probe trigger: parent answer in a code list. */
const codesIn = (key, codes) => ({ op: 'in', ref: key, v: (codes || []).map(String) });

const api = {
  LOGIC_OPS,
  parseRef,
  collectRefs,
  validateLogic,
  evaluateLogic,
  readRef,
  toSurveyJsExpression,
  describeLogic,
  codesIn,
};

export default api;
export { LOGIC_OPS, parseRef, collectRefs, validateLogic, evaluateLogic, readRef, toSurveyJsExpression, describeLogic, codesIn };
